import i18n from '@/utils/i18n';
import moment, { Moment } from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  PushView,
  ScrollView,
  SegmentedControl,
  Select,
  Typography,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { Calendar } from '@usepepper/ui/drafts/calendar';
import {
  DateValidationResult,
  TimeslotCategories,
} from '@/experiences/sales-portal/domain/ordering/blocs/hooks/use-fulfillment-selection';
import { formatTimeslot } from '@/utils/date';
import { FulfillmentName, GetAvailableOrderFulfillmentResponse } from '@/types';
import { ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP } from '@domain/fulfillment-settings/blocs/order-availability';
import { SavedSelectedFulfillment } from '@/experiences/sales-portal/domain/ordering/blocs/stores/selected-fulfillment-store';
import {
  Timeslot,
  TimeZoneMap,
} from '@domain/fulfillment-settings/types/types';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/contexts/theme-context';

interface OrderEntryMobileFulfillmentPushViewProps {
  availableFulfillments: GetAvailableOrderFulfillmentResponse[];
  createIsDateDisabled: (
    type: FulfillmentName | null,
  ) => (date: Moment) => boolean;
  getTimeslotCategories: (
    date: string | null,
    type: FulfillmentName | null,
  ) => TimeslotCategories;
  onClose: () => void;
  onSave: (fulfillment: SavedSelectedFulfillment) => void | Promise<void>;
  open: boolean;
  savedSelectedFulfillment: SavedSelectedFulfillment;
  timezones: TimeZoneMap;
  validateFulfillmentDate: (
    date: string,
    type: FulfillmentName,
  ) => DateValidationResult;
  validateTimeslot: (
    timeslot: Timeslot | null,
    date: Moment,
    type: FulfillmentName,
  ) => Timeslot | null;
}

export const OrderEntryMobileFulfillmentPushView: React.FC<
  OrderEntryMobileFulfillmentPushViewProps
> = ({
  availableFulfillments,
  createIsDateDisabled,
  getTimeslotCategories,
  onClose,
  onSave,
  open,
  savedSelectedFulfillment,
  timezones,
  validateFulfillmentDate,
  validateTimeslot,
}) => {
  const theme = useTheme();
  const insets = useSafeAreaInsets();

  const [isSaving, setIsSaving] = useState(false);
  const [pendingFulfillment, setPendingFulfillment] =
    useState<SavedSelectedFulfillment>(savedSelectedFulfillment);

  useEffect(() => {
    if (open) {
      setPendingFulfillment(savedSelectedFulfillment);
    }
  }, [open, savedSelectedFulfillment]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        onClose();
      }
    },
    [onClose],
  );

  const handleTypeChange = useCallback(
    (type: FulfillmentName) => {
      const fulfillmentSettingsUUID =
        availableFulfillments.find((f) => f.fulfillment_name === type)
          ?.fulfillment_settings_id || null;
      const currentDate = pendingFulfillment.date;

      if (!currentDate) {
        setPendingFulfillment({
          date: '',
          fulfillmentSettingsUUID,
          timeslot: null,
          type,
        });
        return;
      }

      const { isValid, nextValidDate } = validateFulfillmentDate(
        currentDate,
        type,
      );

      if (isValid) {
        setPendingFulfillment({
          date: currentDate,
          fulfillmentSettingsUUID,
          timeslot: validateTimeslot(
            pendingFulfillment.timeslot ?? null,
            moment(currentDate).tz(timezones.restaurant),
            type,
          ),
          type,
        });
        return;
      }

      setPendingFulfillment({
        date: nextValidDate.toISOString(),
        fulfillmentSettingsUUID,
        timeslot: null,
        type,
      });
    },
    [
      availableFulfillments,
      pendingFulfillment.date,
      pendingFulfillment.timeslot,
      timezones.restaurant,
      validateFulfillmentDate,
      validateTimeslot,
    ],
  );

  const handleDateChange = useCallback(
    (date: Moment) => {
      const restaurantDate = moment.tz(
        date.format('YYYY-MM-DD'),
        timezones.restaurant,
      );
      const type =
        pendingFulfillment.type || availableFulfillments[0]?.fulfillment_name;

      setPendingFulfillment({
        date: restaurantDate.toISOString(),
        fulfillmentSettingsUUID:
          availableFulfillments.find((f) => f.fulfillment_name === type)
            ?.fulfillment_settings_id || null,
        timeslot: type
          ? validateTimeslot(
              pendingFulfillment.timeslot ?? null,
              restaurantDate,
              type,
            )
          : null,
        type,
      });
    },
    [
      availableFulfillments,
      pendingFulfillment.timeslot,
      pendingFulfillment.type,
      timezones.restaurant,
      validateTimeslot,
    ],
  );

  const handleDone = useCallback(async () => {
    setIsSaving(true);
    try {
      await onSave(pendingFulfillment);
    } finally {
      setIsSaving(false);
    }
    onClose();
  }, [onClose, onSave, pendingFulfillment]);

  const { availableTimeslots, disabledTimeslots, hasTimeslots } =
    getTimeslotCategories(
      pendingFulfillment.date || null,
      pendingFulfillment.type ?? null,
    );

  const isDateDisabled = useMemo(
    () => createIsDateDisabled(pendingFulfillment.type ?? null),
    [createIsDateDisabled, pendingFulfillment.type],
  );

  const activeDate = pendingFulfillment.date
    ? moment(
        moment(pendingFulfillment.date)
          .tz(timezones.restaurant)
          .format('YYYY-MM-DD'),
      )
    : null;

  const timeslotOptions = [
    ...availableTimeslots.map((option) => ({ isDisabled: false, option })),
    ...disabledTimeslots.map((option) => ({ isDisabled: true, option })),
  ].flatMap(({ isDisabled, option }) => {
    const timeslot = option.metadata?.timeslot;
    if (!timeslot) {
      return [];
    }
    return [
      {
        disabled: isDisabled,
        label: formatTimeslot(timeslot, timezones, true),
        timeslot,
        value: `${timeslot.startTime}|${timeslot.endTime}`,
      },
    ];
  });

  const selectedTimeslotValue = pendingFulfillment.timeslot
    ? `${pendingFulfillment.timeslot.startTime}|${pendingFulfillment.timeslot.endTime}`
    : undefined;

  const handleTimeslotChange = useCallback(
    (value: string) => {
      const picked = timeslotOptions.find((option) => option.value === value);
      if (picked && !picked.disabled) {
        setPendingFulfillment((previous) => ({
          ...previous,
          timeslot: picked.timeslot,
        }));
      }
    },
    [timeslotOptions],
  );

  const isDoneDisabled =
    isSaving || !pendingFulfillment.type || !pendingFulfillment.date;

  return (
    <PushView
      aria-label={i18n.t(
        'sales_portal.order_entry.mobile_header.a11y.fulfillment',
      )}
      onOpenChange={handleOpenChange}
      open={open}
    >
      <YStack paddingHorizontal="$16">
        <PushView.Header backLabel={i18n.t('back')} />
      </YStack>
      <PushView.Body padding="$0">
        <YStack flex={1} minHeight={0}>
          <ScrollView>
            <YStack
              gap="$16"
              paddingBottom={insets.bottom + theme.sk.spacing.spaceMedium}
              paddingHorizontal="$16"
            >
              {availableFulfillments.length > 1 ? (
                <SegmentedControl
                  aria-label={i18n.t('fulfillment_method')}
                  fullWidth
                  items={availableFulfillments
                    .slice(0, 2)
                    .map((fulfillment) => ({
                      label:
                        fulfillment.fulfillment_display_name ||
                        ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP[
                          fulfillment.fulfillment_name
                        ],
                      value: fulfillment.fulfillment_name,
                    }))}
                  onValueChange={(value) =>
                    handleTypeChange(value as FulfillmentName)
                  }
                  value={pendingFulfillment.type ?? undefined}
                />
              ) : null}

              <Calendar
                activeDate={activeDate}
                isDateDisabled={isDateDisabled}
                onDateChange={handleDateChange}
              />

              {hasTimeslots ? (
                <YStack gap="$8">
                  <Typography color="$textSecondary" variant="label">
                    {i18n.t('sales_portal.fulfillment_dropdown.time_label')}
                  </Typography>

                  <Select
                    aria-label={i18n.t(
                      'sales_portal.fulfillment_dropdown.time_label',
                    )}
                    onValueChange={handleTimeslotChange}
                    options={timeslotOptions.map(
                      ({ disabled, label, value }) => ({
                        disabled,
                        label,
                        value,
                      }),
                    )}
                    placeholder={i18n.t(
                      'sales_portal.fulfillment_dropdown.time_placeholder',
                    )}
                    sheetProps={{
                      dismissOnSnapToBottom: true,
                      snapPoints: [80],
                    }}
                    value={selectedTimeslotValue}
                    variant="default"
                  />
                </YStack>
              ) : null}
            </YStack>
          </ScrollView>
        </YStack>
      </PushView.Body>
      <PushView.Footer>
        <Button
          disabled={isDoneDisabled}
          fullWidth
          marginBottom="$24"
          onPress={handleDone}
          size="$lg"
        >
          {i18n.t('done')}
        </Button>
      </PushView.Footer>
    </PushView>
  );
};
